/* Duels — async player-vs-player challenge inbox/outbox.
 *
 * Workflow:
 *   1. Player taps "New Challenge" → picks an opponent + one of their
 *      own battle cards → sends.
 *   2. Opponent sees the challenge in Inbox → accepts with their own
 *      card → the battle runs immediately.
 *   3. Both parties get routed to /battle/live/{battle_id} to watch.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swords, Send, Search, X, Inbox, Share2, Users, Clock, ArrowLeft, Trophy, History } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Skeleton, SkeletonList } from '../../components/Skeleton';
import CountdownPill from '../../components/pvp/CountdownPill';

export default function Duels() {
  const nav = useNavigate();
  const [tab, setTab] = useState('inbox'); // 'inbox' | 'outbox' | 'completed' | 'rivals'
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [rivals, setRivals] = useState([]);
  const [myCards, setMyCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [challengeOpen, setChallengeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [i, o, c, r, cards] = await Promise.all([
        api.get('/duels/inbox').then(r => r.data).catch(() => ({ duels: [] })),
        api.get('/duels/outbox').then(r => r.data).catch(() => ({ duels: [] })),
        api.get('/duels/completed').then(r => r.data).catch(() => ({ duels: [] })),
        api.get('/duels/rivals').then(r => r.data).catch(() => ({ rivals: [] })),
        api.get('/cards?limit=200').then(r => r.data).catch(() => []),
      ]);
      // Split outbox: only "pending" duels I sent are truly Outgoing.
      const pendingOut = (o.duels || []).filter(d => d.status === 'pending');
      setInbox(i.duels || []);
      setOutbox(pendingOut);
      setCompleted(c.duels || []);
      setRivals(r.rivals || []);
      setMyCards((cards || []).filter(x => !x.listed));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 pb-8" data-testid="duels-root">
      <button onClick={() => nav('/battle')} data-testid="duels-back"
        className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
        <ArrowLeft className="w-3 h-3" /> Battle Hub
      </button>
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Battle</p>
        <h2 className="font-serif text-3xl">Friend Duels</h2>
        <p className="text-xs text-muted-foreground mt-1">Challenge a specific Explorer to a private PvP battle.</p>
      </div>

      <button onClick={() => setChallengeOpen(true)} data-testid="duels-new-btn"
        className="w-full px-4 py-3 rounded-full bg-accent text-black text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2">
        <Swords className="w-4 h-4" /> New Challenge
      </button>

      {/* 4-tab strip — horizontally scrollable on very narrow phones. */}
      <div className="flex gap-1 rounded-full bg-black/30 p-1 overflow-x-auto no-scrollbar" data-testid="duels-tabs">
        {[
          { k: 'inbox',     label: 'Inbox',     count: inbox.length,     Icon: Inbox },
          { k: 'outbox',    label: 'Sent',      count: outbox.length,    Icon: Share2 },
          { k: 'completed', label: 'Done',      count: completed.length, Icon: History },
          { k: 'rivals',    label: 'Rivals',    count: rivals.length,    Icon: Users },
        ].map(({ k, label, count, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`duels-tab-${k}`}
            className={`shrink-0 flex-1 py-2 px-2 rounded-full text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 transition-colors whitespace-nowrap ${
              tab === k ? 'bg-accent text-black font-semibold' : 'text-muted-foreground hover:text-white'
            }`}>
            <Icon className="w-3 h-3 shrink-0" /> {label}
            {count > 0 && <span className={`text-[9px] font-mono tabular-nums shrink-0 ${tab === k ? '' : 'opacity-60'}`}>{count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonList rows={3} avatar />
      ) : tab === 'inbox' ? (
        <InboxList duels={inbox} myCards={myCards} onRefresh={load} nav={nav} />
      ) : tab === 'outbox' ? (
        <OutboxList duels={outbox} nav={nav} />
      ) : tab === 'completed' ? (
        <CompletedList duels={completed} nav={nav} />
      ) : (
        <RivalsList rivals={rivals} nav={nav} onChallenge={() => setChallengeOpen(true)} />
      )}

      {challengeOpen && (
        <NewChallengeModal
          myCards={myCards}
          onClose={() => setChallengeOpen(false)}
          onCreated={() => { setChallengeOpen(false); load(); }}
        />
      )}
    </div>
  );
}


/* --- Inbox ------------------------------------------------------------ */
function InboxList({ duels, myCards, onRefresh, nav }) {
  if (!duels.length) return (
    <div className="rounded-2xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground" data-testid="duels-inbox-empty">
      No pending challenges. Ask a friend to send one!
    </div>
  );
  return (
    <div className="space-y-2" data-testid="duels-inbox-list">
      {duels.map(d => <InboxRow key={d.duel_id} duel={d} myCards={myCards} onRefresh={onRefresh} nav={nav} />)}
    </div>
  );
}

function InboxRow({ duel, myCards, onRefresh, nav }) {
  const [picking, setPicking] = useState(false);
  const [selCard, setSelCard] = useState('');
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (!selCard) return toast.error('Pick one of your cards first');
    setBusy(true);
    try {
      const r = await api.post(`/duels/${duel.duel_id}/accept`, { opponent_card_id: selCard });
      const bid = r.data?.battle_id;
      if (bid) {
        toast.success(r.data?.you_won ? 'Victory!' : 'You lost — well fought.');
        nav(`/battle/live/${bid}`, { state: { battle: r.data.battle }, replace: false });
      } else {
        onRefresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept challenge');
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/duels/${duel.duel_id}/decline`);
      onRefresh();
    } catch { toast.error('Decline failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-amber-400/20 bg-amber-500/5 p-3 space-y-2" data-testid={`duel-inbox-${duel.duel_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Swords className="w-4 h-4 text-amber-300" />
        <p className="text-xs">Challenge from an Explorer</p>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" />{new Date(duel.created_at).toLocaleDateString()}
        </span>
        {duel.expires_at && <CountdownPill expiresAt={duel.expires_at} testId={`inbox-countdown-${duel.duel_id}`} />}
      </div>
      {duel.message && (
        <p className="text-xs italic text-muted-foreground border-l-2 border-amber-400/40 pl-2">
          "{duel.message}"
        </p>
      )}
      {!picking ? (
        <div className="flex gap-2">
          <button onClick={() => setPicking(true)} data-testid={`duel-accept-btn-${duel.duel_id}`}
            className="flex-1 px-3 py-2 rounded-full bg-accent text-black text-[11px] font-semibold uppercase tracking-widest">
            Accept
          </button>
          <button onClick={decline} data-testid={`duel-decline-btn-${duel.duel_id}`}
            disabled={busy}
            className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest">
            Decline
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Pick your fighter</p>
          <select value={selCard} onChange={e => setSelCard(e.target.value)}
            data-testid={`duel-card-select-${duel.duel_id}`}
            className="w-full bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-xs">
            <option value="">Choose a battle card…</option>
            {myCards.map(c => (
              <option key={c.card_id} value={c.card_id}>
                {c.species_common_name} · {c.rarity}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button onClick={accept} disabled={busy || !selCard} data-testid={`duel-confirm-accept-${duel.duel_id}`}
              className="flex-1 px-3 py-2 rounded-full bg-accent text-black text-[11px] font-semibold uppercase tracking-widest disabled:opacity-50">
              {busy ? 'Fighting…' : 'Fight!'}
            </button>
            <button onClick={() => setPicking(false)} className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* --- Outbox ------------------------------------------------------------ */
function OutboxList({ duels, nav }) {
  if (!duels.length) return (
    <div className="rounded-2xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground" data-testid="duels-outbox-empty">
      No outgoing challenges. When someone accepts one you sent, it moves to Completed.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="duels-outbox-list">
      {duels.map(d => (
        <div key={d.duel_id} className="rounded-2xl border border-amber-400/15 bg-amber-500/[0.04] p-3 flex items-center gap-3" data-testid={`duel-outbox-${d.duel_id}`}>
          <div className="flex-1 min-w-0">
            <p className="text-xs flex items-center gap-2 flex-wrap">
              <span>Awaiting reply</span>
              {d.expires_at && <CountdownPill expiresAt={d.expires_at} testId={`outbox-countdown-${d.duel_id}`} />}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">Sent {new Date(d.created_at).toLocaleDateString()}</p>
            {d.message && (
              <p className="text-[11px] italic text-muted-foreground mt-1 truncate">"{d.message}"</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


/* --- Completed -------------------------------------------------------- */
function CompletedList({ duels, nav }) {
  if (!duels.length) return (
    <div className="rounded-2xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground" data-testid="duels-completed-empty">
      No completed duels yet. Send or accept your first challenge!
    </div>
  );
  return (
    <div className="space-y-2" data-testid="duels-completed-list">
      {duels.map(d => {
        const label = d.status === 'accepted' ? (d.battle_id ? 'Battle · ' : '') + 'resolved'
          : d.status === 'declined' ? 'declined'
          : d.status === 'expired' ? 'expired unanswered' : d.status;
        const tone = d.status === 'accepted' ? 'border-emerald-400/20 bg-emerald-500/5'
          : d.status === 'declined' ? 'border-rose-400/20 bg-rose-500/5'
          : 'border-white/5 bg-card';
        return (
          <div key={d.duel_id} className={`rounded-2xl border p-3 flex items-center gap-3 ${tone}`}
               data-testid={`duel-completed-${d.duel_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-xs capitalize">{label}</p>
              <p className="text-[10px] text-muted-foreground">
                {new Date(d.accepted_at || d.declined_at || d.created_at).toLocaleDateString()}
              </p>
            </div>
            {d.battle_id && (
              <button onClick={() => nav(`/battle/live/${d.battle_id}`)}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest"
                data-testid={`duel-completed-recap-${d.duel_id}`}>
                Recap
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* --- Rivals ----------------------------------------------------------- */
function RivalsList({ rivals, nav, onChallenge }) {
  if (!rivals.length) return (
    <div className="rounded-2xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground" data-testid="duels-rivals-empty">
      No rivals yet. Send your first challenge to start a rivalry!
    </div>
  );
  return (
    <div className="space-y-2" data-testid="duels-rivals-list">
      {rivals.map(r => {
        const total = (r.wins || 0) + (r.losses || 0);
        const winPct = total > 0 ? Math.round((r.wins / total) * 100) : 0;
        const badge = total === 0 ? 'no result yet'
          : r.wins > r.losses ? `You lead ${r.wins}-${r.losses}`
          : r.wins === r.losses ? `Tied ${r.wins}-${r.losses}`
          : `They lead ${r.losses}-${r.wins}`;
        const badgeTone = r.wins > r.losses ? 'text-emerald-300' : r.wins === r.losses ? 'text-muted-foreground' : 'text-rose-300';
        return (
          <div key={r.rival_user_id} className="rounded-2xl border border-white/10 bg-card p-3 flex items-center gap-3"
               data-testid={`duel-rival-${r.rival_user_id}`}>
            <img
              src={r.picture || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 40 40%22><rect fill=%22%23222%22 width=%2240%22 height=%2240%22/></svg>'}
              alt="" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
              className="w-11 h-11 rounded-full object-cover border border-white/15 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-base truncate">{r.nickname || 'Explorer'}</p>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <span className={`text-[10px] uppercase tracking-widest ${badgeTone}`}>{badge}</span>
                {r.pending > 0 && (
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300">
                    {r.pending} pending
                  </span>
                )}
              </div>
              {total > 0 && (
                <div className="mt-1 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-rose-400"
                       style={{ width: `${winPct}%` }} />
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono tabular-nums text-lg leading-none">{r.wins}<span className="text-white/40 text-sm"> · {r.losses}</span></p>
              <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
                {r.battles} {r.battles === 1 ? 'battle' : 'battles'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* --- New Challenge Modal ---------------------------------------------- */
function NewChallengeModal({ myCards, onClose, onCreated }) {
  const [q, setQ] = useState('');
  const [explorers, setExplorers] = useState([]);
  const [pickedUser, setPickedUser] = useState(null);
  const [pickedCard, setPickedCard] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);

  const doSearch = async (query) => {
    setSearchBusy(true);
    try {
      const r = await api.get(`/duels/opponents/search?q=${encodeURIComponent(query)}`);
      setExplorers(r.data.explorers || []);
    } finally { setSearchBusy(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => doSearch(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const send = async () => {
    if (!pickedUser) return toast.error('Pick an opponent first');
    if (!pickedCard) return toast.error('Pick your fighter card');
    setBusy(true);
    try {
      await api.post('/duels/challenge', {
        opponent_user_id: pickedUser.user_id,
        challenger_card_id: pickedCard,
        message: message.slice(0, 200),
      });
      toast.success('Challenge sent!');
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || 'Challenge failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const cardsPreview = useMemo(() => myCards.slice(0, 30), [myCards]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-end sm:items-center justify-center p-3"
      data-testid="duel-new-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-3xl bg-card border border-white/10 p-4 space-y-3 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-2">
          <Swords className="w-4 h-4 text-amber-300" />
          <h3 className="font-serif text-xl flex-1">New Challenge</h3>
          <button onClick={onClose} data-testid="duel-new-close"
            className="w-8 h-8 rounded-full glass border border-white/10 flex items-center justify-center">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Opponent search */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Opponent</p>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by nickname or name…"
              data-testid="duel-search-input"
              className="w-full bg-black/50 border border-white/10 rounded-full pl-9 pr-3 py-2 text-xs outline-none focus:border-amber-400/50"
            />
          </div>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1" data-testid="duel-search-results">
            {searchBusy && <Skeleton className="h-10 rounded-xl" />}
            {!searchBusy && explorers.length === 0 && (
              <p className="text-[11px] text-muted-foreground py-1">No matching explorers yet.</p>
            )}
            {explorers.map(u => {
              const active = pickedUser?.user_id === u.user_id;
              const label = u.nickname || u.display_name || u.name || 'Explorer';
              return (
                <button key={u.user_id}
                  onClick={() => setPickedUser(u)}
                  data-testid={`duel-pick-user-${u.user_id}`}
                  className={`w-full flex items-center gap-2 p-2 rounded-xl border transition ${active ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/5 bg-black/30 hover:border-white/20'}`}>
                  <div className="w-8 h-8 rounded-full bg-white/10 overflow-hidden flex items-center justify-center text-xs">
                    {u.picture ? <img src={u.picture} alt={label} className="w-full h-full object-cover" /> : <Users className="w-3.5 h-3.5" />}
                  </div>
                  <span className="text-xs flex-1 text-left truncate">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Card picker */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Your fighter</p>
          <select
            value={pickedCard}
            onChange={e => setPickedCard(e.target.value)}
            data-testid="duel-my-card-select"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-2 py-2 text-xs">
            <option value="">Pick a battle card…</option>
            {cardsPreview.map(c => (
              <option key={c.card_id} value={c.card_id}>
                {c.species_common_name} · {c.rarity} · rating {c.battle_rating || '—'}
              </option>
            ))}
          </select>
        </div>

        {/* Optional message */}
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Trash talk (optional)</p>
          <input
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, 200))}
            placeholder="Say something to your rival…"
            maxLength={200}
            data-testid="duel-message-input"
            className="w-full bg-black/50 border border-white/10 rounded-full px-3 py-2 text-xs outline-none focus:border-amber-400/50"
          />
        </div>

        <button onClick={send} disabled={busy || !pickedUser || !pickedCard}
          data-testid="duel-send-btn"
          className="w-full px-4 py-3 rounded-full bg-accent text-black text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> {busy ? 'Sending…' : 'Send challenge'}
        </button>
      </motion.div>
    </motion.div>
  );
}
